/**
 * Round scene — main gameplay scene for Who Am I? (continuous-play letter-reveal).
 *
 * Layout (1920×1080):
 *   • Big centered title "WHO AM I?" above the letter tile rows.
 *   • Letter tiles laid out by `layoutLetterRows`:
 *       - 2-word subjects → ALWAYS one word per line.
 *       - 3+ word subjects → pack words on a line until letters >9, then wrap.
 *       - Single word >9 letters → scale ALL tiles down uniformly to fit.
 *     Container is capped at 1180px so it never overlaps the right-side LB.
 *   • Footer: "Type your guess in chat" + a stack of correct-answer toasts on
 *     the right showing each player's contribution as the round continues.
 *
 * Top-left countdown is owned by main.ts (60s round timer).
 *
 * Round scene removes:
 *   - "ROUND N OF M" eyebrow
 *   - The progress pips row
 *   - The inline "next letter in N s" ticker
 */

import type { RoundState } from '../state/types.js';

export interface RoundHandle {
  el: HTMLDivElement;
  setRound(r: RoundState): void;
  setVisible(v: boolean): void;
  /** Called periodically (5Hz) — kept for compatibility; ticker no longer used. */
  tick(): void;
}

const STYLES = `
  @keyframes wai-tile-reveal {
    0%   { transform: rotateY(180deg) scale(0.6); opacity: 0; filter: blur(8px); }
    50%  { transform: rotateY(20deg)  scale(1.18); opacity: 1; filter: blur(0); }
    100% { transform: rotateY(0)      scale(1.0); }
  }
  @keyframes wai-tile-glow {
    0%   { box-shadow: 0 0 0 rgba(0,240,255,0); }
    50%  { box-shadow: 0 0 36px rgba(0,240,255,0.85); }
    100% { box-shadow: 0 0 0 rgba(0,240,255,0); }
  }
  @keyframes wai-tile-idle-pulse {
    0%, 100% { box-shadow: 0 6px 18px rgba(0,0,0,0.45), inset 0 0 0 rgba(0,240,255,0); }
    50%      { box-shadow: 0 8px 24px rgba(0,0,0,0.55), inset 0 0 24px rgba(0,240,255,0.35); }
  }
  @keyframes wai-toast-in {
    0%   { opacity: 0; transform: translateX(40px) scale(0.9); }
    60%  { opacity: 1; transform: translateX(-4px) scale(1.05); }
    100% { opacity: 1; transform: translateX(0)    scale(1.0); }
  }
  .wai-round {
    position: absolute; inset: 0;
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
  }
  /* Big centered subject title above the tiles. */
  .wai-round-title {
    /* Reserve space for the top-left countdown widget (~240px wide) AND the
       right-side leaderboard panel at left=1500..1880 (~420px). The result
       sits centered between them. */
    padding: 80px 460px 8px 280px;
    text-align: center;
    font-family: 'Bebas Neue','Oswald',sans-serif;
    font-size: 96px;
    line-height: 0.95;
    letter-spacing: 0.1em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 40px rgba(255,196,61,0.35);
  }

  /* Category/type pill — pinned ABOVE the letter tiles, below the title.
     Stays in this row regardless of how the tile container scales down. */
  .wai-round-category {
    padding: 4px 460px 18px 280px;
    text-align: center;
    /* Empty category collapses to zero so layout doesn't reserve dead space. */
    min-height: 0;
  }
  .wai-round-category-pill {
    display: inline-block;
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    font-size: 32px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--sg-primary, #FFC43D);
    background: rgba(6,12,233,0.45);
    border: 2px solid rgba(255,196,61,0.7);
    border-radius: 999px;
    padding: 6px 22px 7px;
    box-shadow: 0 0 24px rgba(255,196,61,0.2), inset 0 0 12px rgba(0,240,255,0.18);
    text-shadow: 0 2px 0 var(--sg-secondary, #060CE9);
  }
  .wai-round-category[data-empty="1"] .wai-round-category-pill {
    display: none;
  }

  .wai-round-body {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    /* leave room for side-leaderboard at right=1500..1880 (380w) plus gutter */
    padding: 12px 460px 20px 80px;
    min-height: 0;
  }

  /* --- Letter tiles --- */
  .wai-tiles {
    /* Vertical stack of rows; each .wai-row is a horizontal flex of tiles. */
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 18px;
    /* Cap so we never overlap the right-side leaderboard. */
    max-width: 1180px;
    width: 100%;
    margin: 0 auto;
  }
  .wai-row {
    display: flex; flex-wrap: nowrap;
    align-items: center; justify-content: center;
    gap: 10px;
  }
  .wai-tile {
    display: inline-flex;
    align-items: center; justify-content: center;
    background: linear-gradient(180deg, rgba(6,12,233,0.85) 0%, rgba(3,4,40,0.95) 100%);
    border: 4px solid var(--sg-accent, #00F0FF);
    border-radius: 14px;
    color: var(--sg-primary, #FFC43D);
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    text-transform: uppercase;
    box-shadow: 0 6px 18px rgba(0,0,0,0.45);
    transition: background 240ms ease, color 240ms ease, border-color 240ms ease;
    transform-style: preserve-3d;
    flex: 0 0 auto;
  }
  .wai-tile.hidden {
    color: rgba(255,196,61,0.55);
    border-color: rgba(0,240,255,0.45);
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.85) 100%);
  }
  .wai-tile.revealed {
    color: var(--sg-primary, #FFC43D);
    border-color: var(--sg-accent, #00F0FF);
    text-shadow: 0 4px 0 var(--sg-secondary, #060CE9), 0 0 24px rgba(0,240,255,0.55);
  }
  .wai-tile.just-revealed {
    animation:
      wai-tile-reveal 540ms cubic-bezier(0.18, 0.85, 0.4, 1.0) both,
      wai-tile-glow 1.4s ease-out 540ms 1;
  }
  .wai-tile.punct {
    background: transparent;
    border: none;
    color: var(--sg-primary, #FFC43D);
    box-shadow: none;
    text-shadow: 0 4px 0 var(--sg-secondary, #060CE9);
  }
  .wai-tiles.full-revealed .wai-tile.revealed {
    animation: wai-tile-idle-pulse 2.2s ease-in-out infinite;
  }

  /* Footer with chat hint + correct-answer toasts. */
  .wai-foot {
    padding: 12px 460px 24px 80px;
    display: flex; justify-content: space-between; align-items: flex-end; gap: 24px;
    font-family: 'Inter', system-ui, sans-serif;
    color: #E2E8F0;
    font-size: 22px;
    letter-spacing: 0.06em;
    min-height: 56px;
  }
  .wai-foot code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.15);
    padding: 4px 10px;
    border-radius: 5px;
    font-family: ui-monospace, monospace;
    font-size: 0.95em;
  }
  .wai-toasts {
    display: flex; flex-wrap: wrap; gap: 8px 12px;
    justify-content: flex-end; align-items: flex-end;
    max-width: 60%;
  }
  .wai-toast {
    background: var(--sg-celebration, #22C55E);
    color: #0A0E27;
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    font-size: 18px;
    letter-spacing: 0.05em;
    padding: 6px 12px;
    border-radius: 999px;
    box-shadow: 0 4px 14px rgba(34,197,94,0.45);
    animation: wai-toast-in 380ms cubic-bezier(0.18,0.85,0.4,1.0) both;
    white-space: nowrap;
  }
  .wai-toast .pts {
    margin-left: 6px;
    color: #14532D;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px;
    letter-spacing: 0.04em;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('wai-round-style')) return;
  const s = document.createElement('style');
  s.id = 'wai-round-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

// ---------------------------------------------------------------------------
// Letter layout helper (pure, exported for tests)
// ---------------------------------------------------------------------------

export interface LayoutTile {
  kind: 'letter' | 'punct';
  ch: string;
}

export interface LayoutRow {
  /**
   * Tokens that make up this visual row. A token is one word (sequence of
   * adjacent non-space chars). Word boundaries are kept as separate tokens so
   * the renderer can insert a small gap between them on a single line.
   */
  tokens: Array<LayoutTile[]>;
}

export interface LayoutResult {
  rows: LayoutRow[];
  /** Tile width / height / font-size in stage px. */
  tileSize: { w: number; h: number; font: number };
}

const STAGE_LETTER_BUDGET_PX = 1180;
const BASE_TILE = { w: 110, h: 134, font: 96 } as const;
// Per-tile horizontal stride in the row: tile width + gap between tiles.
const TILE_GAP = 10;
// Extra breathing room added between WORD tokens on a multi-word line.
const WORD_GAP_EXTRA = 28;

/**
 * Split the rendered string into per-word tile lists. The backend joins each
 * char with a single space + emits a literal-space token for word breaks,
 * which becomes a double-space in the output. We split on " " and treat empty
 * tokens as word breaks.
 */
export function parseRendered(rendered: string): Array<LayoutTile[]> {
  const tokens = rendered.split(' ');
  const words: Array<LayoutTile[]> = [];
  let current: LayoutTile[] = [];
  for (const t of tokens) {
    if (t === '') {
      // Word break.
      if (current.length > 0) {
        words.push(current);
        current = [];
      }
      continue;
    }
    if (t === '_') current.push({ kind: 'letter', ch: '_' });
    else if (/^[A-Za-z]$/.test(t)) current.push({ kind: 'letter', ch: t });
    else current.push({ kind: 'punct', ch: t });
  }
  if (current.length > 0) words.push(current);
  return words;
}

function tilesInWord(word: LayoutTile[]): number {
  // We size punctuation as a normal slot too; keeps the row math simple.
  return word.length;
}

function rowWidth(tileCount: number, tileW: number, wordGaps: number): number {
  // tile_count tiles, (tile_count-1) inter-tile gaps, plus N word-spacers.
  const interTile = Math.max(0, tileCount - 1) * TILE_GAP;
  const interWord = wordGaps * WORD_GAP_EXTRA;
  return tileCount * tileW + interTile + interWord;
}

/**
 * Compute the row layout for a subject given its rendered string.
 *
 * Rules (per spec):
 *   - 2-word subjects: ALWAYS one word per line (regardless of length).
 *   - 3+ word subjects: pack words onto one line until total LETTER count
 *     in that line would exceed 9, then start a new line.
 *   - Single word > 9 letters: keep it on one line; scale ALL tiles down
 *     uniformly so the longest line fits within the container width.
 */
export function layoutLetterRows(rendered: string): LayoutResult {
  const words = parseRendered(rendered);
  if (words.length === 0) {
    return { rows: [], tileSize: { ...BASE_TILE } };
  }

  let rows: LayoutRow[];
  if (words.length === 2) {
    rows = words.map((w) => ({ tokens: [w] }));
  } else if (words.length === 1) {
    rows = [{ tokens: [words[0]!] }];
  } else {
    // 3+ words: pack until letter count > 9.
    rows = [];
    let line: LayoutTile[][] = [];
    let lineLetters = 0;
    for (const w of words) {
      const wlen = tilesInWord(w);
      if (line.length > 0 && lineLetters + wlen > 9) {
        rows.push({ tokens: line });
        line = [];
        lineLetters = 0;
      }
      line.push(w);
      lineLetters += wlen;
    }
    if (line.length > 0) rows.push({ tokens: line });
  }

  // Compute the longest line's width at base tile size, scale down if needed.
  let maxTilesPerRow = 0;
  let maxRowW = 0;
  for (const row of rows) {
    const totalTiles = row.tokens.reduce((s, w) => s + tilesInWord(w), 0);
    const wordGaps = Math.max(0, row.tokens.length - 1);
    const w = rowWidth(totalTiles, BASE_TILE.w, wordGaps);
    if (totalTiles > maxTilesPerRow) maxTilesPerRow = totalTiles;
    if (w > maxRowW) maxRowW = w;
  }

  let scale = 1;
  if (maxRowW > STAGE_LETTER_BUDGET_PX) {
    scale = STAGE_LETTER_BUDGET_PX / maxRowW;
  }
  // Floor at a readable minimum (~36px font) so absurd 20+ letter words still
  // produce visible tiles. The container clamp dominates anything reasonable.
  const minScale = 36 / BASE_TILE.font;
  if (scale < minScale) scale = minScale;

  const tileSize = {
    w: Math.round(BASE_TILE.w * scale),
    h: Math.round(BASE_TILE.h * scale),
    font: Math.round(BASE_TILE.font * scale),
  };
  return { rows, tileSize };
}

// ---------------------------------------------------------------------------
// Scene factory
// ---------------------------------------------------------------------------

export function createRound(stage: HTMLElement): RoundHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'wai-round';
  el.setAttribute('data-scene', 'round');
  el.setAttribute('data-testid', 'wai-round');

  const title = document.createElement('div');
  title.className = 'wai-round-title';
  title.setAttribute('data-testid', 'wai-round-title');
  title.textContent = 'WHO AM I?';

  // Category/type label, pinned ABOVE the letter tiles. Owns its own grid row
  // so it never shifts when `.wai-tiles` scales down for long subjects.
  const categoryRow = document.createElement('div');
  categoryRow.className = 'wai-round-category';
  categoryRow.setAttribute('data-testid', 'wai-round-category');
  categoryRow.setAttribute('data-empty', '1');
  const categoryPill = document.createElement('span');
  categoryPill.className = 'wai-round-category-pill';
  categoryRow.appendChild(categoryPill);

  const body = document.createElement('div');
  body.className = 'wai-round-body';
  const tilesEl = document.createElement('div');
  tilesEl.className = 'wai-tiles';
  tilesEl.setAttribute('data-testid', 'wai-tiles');
  body.appendChild(tilesEl);

  const foot = document.createElement('div');
  foot.className = 'wai-foot';
  const hint = document.createElement('span');
  hint.innerHTML = `Type your guess in <code>chat</code>`;
  const toasts = document.createElement('div');
  toasts.className = 'wai-toasts';
  toasts.setAttribute('data-testid', 'wai-toasts');
  foot.appendChild(hint);
  foot.appendChild(toasts);

  el.appendChild(title);
  el.appendChild(categoryRow);
  el.appendChild(body);
  el.appendChild(foot);
  stage.appendChild(el);

  let cur: RoundState | null = null;
  let lastSubjectId: string | null = null;
  let lastRendered: string = '';
  let renderedToastKeys = new Set<string>();

  function rebuildTiles(): void {
    if (!cur) return;
    const { rows, tileSize } = layoutLetterRows(cur.rendered);
    const sizeStyle = `width:${tileSize.w}px; height:${tileSize.h}px; font-size:${tileSize.font}px;`;

    // Detect just-revealed positions for the pop animation.
    const flatNew = rows.flatMap((r) => r.tokens.flat());
    const prevRows = layoutLetterRows(lastRendered).rows;
    const flatOld = prevRows.flatMap((r) => r.tokens.flat());
    const justRevealedIdx = new Set<number>();
    if (flatOld.length === flatNew.length) {
      for (let i = 0; i < flatNew.length; i++) {
        const a = flatNew[i]!;
        const b = flatOld[i]!;
        if (a.kind === 'letter' && a.ch !== '_' && b.kind === 'letter' && b.ch === '_') {
          justRevealedIdx.add(i);
        }
      }
    }

    tilesEl.innerHTML = '';
    let visualIdx = 0;
    for (const row of rows) {
      const rowEl = document.createElement('div');
      rowEl.className = 'wai-row';
      for (let wi = 0; wi < row.tokens.length; wi++) {
        const word = row.tokens[wi]!;
        for (const t of word) {
          const div = document.createElement('div');
          if (t.kind === 'punct') {
            div.className = 'wai-tile punct';
            div.style.cssText = `width:${tileSize.w * 0.6}px; height:${tileSize.h}px; font-size:${tileSize.font}px;`;
            div.textContent = t.ch;
            rowEl.appendChild(div);
            visualIdx += 1;
            continue;
          }
          const isRevealed = t.ch !== '_';
          const justRevealed = justRevealedIdx.has(visualIdx);
          div.className = `wai-tile ${isRevealed ? 'revealed' : 'hidden'}${justRevealed ? ' just-revealed' : ''}`;
          div.style.cssText = sizeStyle;
          div.textContent = t.ch === '_' ? '_' : t.ch;
          div.setAttribute('data-tile-index', String(visualIdx));
          rowEl.appendChild(div);
          visualIdx += 1;
        }
        // Word spacer between words on a multi-word line.
        if (wi < row.tokens.length - 1) {
          const sp = document.createElement('div');
          sp.style.cssText = `flex:0 0 ${WORD_GAP_EXTRA}px; height:1px;`;
          rowEl.appendChild(sp);
        }
      }
      tilesEl.appendChild(rowEl);
    }

    const allRevealed = cur.revealedCount >= cur.totalLetters && cur.totalLetters > 0;
    tilesEl.classList.toggle('full-revealed', allRevealed);
    lastRendered = cur.rendered;
  }

  function rebuildToasts(): void {
    if (!cur) {
      toasts.innerHTML = '';
      renderedToastKeys = new Set();
      return;
    }
    const wantedKeys = new Set(cur.corrects.map((c) => `${c.player_id}:${c.rank}`));
    // If the round changed (or corrects array shrank), reset.
    if (renderedToastKeys.size > wantedKeys.size) {
      toasts.innerHTML = '';
      renderedToastKeys = new Set();
    }
    for (const c of cur.corrects) {
      const key = `${c.player_id}:${c.rank}`;
      if (renderedToastKeys.has(key)) continue;
      const t = document.createElement('div');
      t.className = 'wai-toast';
      t.setAttribute('data-testid', 'wai-toast');
      // No names mid-round (no-names policy, commit 1fc84bd) — rank + points
      // only. Mirrors Anagram's #N +points chips.
      t.innerHTML = `#${c.rank} <span class="pts">+${c.points}</span>`;
      toasts.appendChild(t);
      renderedToastKeys.add(key);
    }
  }

  function rebuildCategory(): void {
    const cat = (cur?.category ?? '').trim();
    if (!cat) {
      categoryRow.setAttribute('data-empty', '1');
      categoryPill.textContent = '';
      return;
    }
    categoryRow.removeAttribute('data-empty');
    // Kebab → space so "historical-figure" reads as HISTORICAL FIGURE.
    categoryPill.textContent = cat.replace(/[-_]+/g, ' ').toUpperCase();
  }

  function update(): void {
    if (!cur) return;
    if (cur.subjectId !== lastSubjectId) {
      lastSubjectId = cur.subjectId;
      lastRendered = '';
      // New round: reset toasts.
      toasts.innerHTML = '';
      renderedToastKeys = new Set();
    }
    rebuildCategory();
    rebuildTiles();
    rebuildToasts();
  }

  return {
    el,
    setRound(r) {
      cur = r;
      update();
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
    },
    tick() {
      // No-op under continuous play (the inline ticker was removed; the
      // top-left countdown widget owns the visible round timer).
    },
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}
